import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { MatOptionModule } from "@angular/material/core";
import { MatIconModule } from "@angular/material/icon";
import { MatSelectModule } from "@angular/material/select";
import { MaterialModule } from "../../../core/shared/material.module";
import { PrimeNgModule } from "../../../core/shared/primeng.module";
import { SharedComponents } from "../../shared.module";
/**
 * Component for rendering a customizable bar chart with optional horizontal orientation
 * and stacked layout using PrimeNG and Chart.js.
 */
@Component({
  selector: "app-bar-chart",
  standalone: true,
  imports: [
    PrimeNgModule,
    CommonModule,
    ...SharedComponents,
    MatIconModule,
    MaterialModule,
    MatOptionModule,
    MatSelectModule,
  ],
  templateUrl: "./bar-chart.component.html",
  styleUrl: "./bar-chart.component.scss",
})
export class BarChartComponent {
  @Input() chartType: any = "bar";
  @Input() data: any = {};
  @Input() isHorizontal?: boolean = false;
  @Input() isSeverity?: boolean = false;
  @Input() metricDisplayName: string = "";

  chartData: any;
  chartOptions: any;
  labels: string[] = [];
  values: number[] = [];
  rootStyles = getComputedStyle(document.documentElement);
  dataset: any;
  categories: any;
  note: any;
  selectedTestSuite: string = "";
  testSuites: any = [];
  isOverallActive: boolean = true;

  /**
   * Lifecycle hook to initialize chart data and configuration.
   */
  ngOnInit() {
    // this.note = this.isHorizontal ? "This chart shows the top 10 defect categories with the highest number of defects": "";
    this.dataset = this.data?.data ? this.data?.data : {};
    this.getChartDataset(
      this.data?.meta[0]?.chartType == "horizontal" ? true : false
    );
    this.chartOptions = {
      indexAxis: this.data?.meta[0]?.chartType == "horizontal" ? "y" : "x", // Ensures horizontal bar chart
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          title: {
            display: true,
            text: this.data?.meta[0]?.x ? this.data?.meta[0]?.x : "", // X-axis title from your data
          },
          beginAtZero: true,
          ticks: {
            color: this.rootStyles.getPropertyValue("--charcoal").trim(),
            font: {
              size: 12,
            },
            precision: 0, // Ensures no decimal values
            stepSize: 5,
            // callback: (value: any, index: any, values: any) => {
            //   const label = this.labels[index]; // Get the actual label
            //   return typeof label === "string"
            //     ? this.ellipsizeLabel(label, 15)
            //     : label;
            // },
          },
          grid: {
            color: this.rootStyles.getPropertyValue("--").trim(),
            borderDash: [6, 4],
          },
        },
        y: {
          title: {
            display: true,
            text: this.data?.meta[0]?.y ? this.data?.meta[0]?.y : "", // Y-axis title from your data
          },
          ticks: {
            autoSkip: false, // Disable auto skipping of ticks for better visibility
            color: this.rootStyles.getPropertyValue("--coal").trim(),
            font: {
              size: 12,
            },
            precision: 0,
            align: "left",
            // callback: (value: any, index: any, values: any) => {
            //   const label = this.labels[index]; // Get the actual label
            //   return typeof label === "string"
            //     ? this.ellipsizeLabel(label, 15)
            //     : label;
            // },
          },
          grid: {
            color: this.rootStyles.getPropertyValue("--grey3").trim(),
            borderDash: [6, 4],
          },
          position: "left",
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const yAxisLabel = this.data?.meta[0]?.y || "Count";
              const value = context?.raw || 0;
              return `${yAxisLabel}: ${value}`; // Tooltip formatting
            },
          },
        },
        datalabels: {
          display: false, // Control the display of data labels on bars
        },
      },
    };
  }

  /*
   * ellipsis the long labels
   */
  ellipsizeLabel(label: string, maxLength: number): string {
    return label.length > maxLength
      ? label.substring(0, maxLength) + "..."
      : label;
  }

  getOverallData() {
    const categoryTotals: { [key: string]: number } = {};

    // Iterate over test suites
    Object.values(this.dataset).forEach((testSuite: any) => {
      Object.entries(testSuite).forEach(([category, count]) => {
        categoryTotals[category] =
          (categoryTotals[category] || 0) + (count as number);
      });
    });

    // Store results in lists for charting
    // this.labels = Object.keys(categoryTotals);
    this.labels = Object.keys(categoryTotals).map((word) =>
      this.capitalizeFirstLetter(word)
    );
    this.values = Object.values(categoryTotals);
  }

  onSelectionChange() {
    this.isOverallActive = false;
    this.getChartDataset(false);
  }

  showOverallData() {
    this.isOverallActive = true;
    this.selectedTestSuite = "";
    this.getChartDataset(true);
  }

  getTestSuiteCounts(testSuiteName: string) {
    const testSuiteData = this.dataset[testSuiteName];
    if (testSuiteData) {
      // this.labels = Object.keys(testSuiteData);
      this.labels = Object.keys(testSuiteData).map((word) =>
        this.capitalizeFirstLetter(word)
      );

      this.values = Object.values(testSuiteData);
    }
  }

  /**
   * Extracts and processes the chart dataset based on input data and orientation.
   */
  getChartDataset(isOverall?: boolean) {
    if (this.isObjectEmpty(this.dataset)) {
      return;
    }
    if (this.data?.meta[0]?.chartType == "horizontal") {
      //for defect severity
      this.testSuites = Object.keys(this.dataset);

      if (isOverall) {
        this.getOverallData();
      } else {
        this.getTestSuiteCounts(this.selectedTestSuite);
      }

      return (this.chartData = {
        labels: this.labels,
        datasets: [
          {
            data: this.values,
            backgroundColor: this.values.map((_, index) =>
              this.getDynamicColor(index)
            ),
            barPercentage: 0.8,
            categoryPercentage: 0.85,
            barThickness: 100,
            borderRadius: { topLeft: 6, topRight: 6 },
            borderSkipped: false,
          },
        ],
      });
    } else {
      if (this.dataset?.labels !== undefined) {
        return (this.chartData = {
          labels: this.dataset?.labels,
          datasets: this.dataset.datasets,
        });
      } else if (this.data?.meta[0]?.chartType == "group_chart") {
        //test execution over-time bar chart

        const labels = Object.keys(this.dataset);

        // Extract fail & pass values separately
        const failData = labels.map((date) => this.dataset[date]?.fail ?? 0);
        const passData = labels.map((date) => this.dataset[date]?.pass ?? 0);

        return (this.chartData = {
          labels: labels,
          datasets: [
            {
              label: "Fail",
              data: failData,
              backgroundColor: this.getDynamicColor(3),
              barPercentage: 0.8,
              categoryPercentage: 0.85,
              barThickness: 100,
              borderRadius: { topLeft: 6, topRight: 6 },
              borderSkipped: false,
            },
            {
              label: "Pass",
              data: passData,
              backgroundColor: this.getDynamicColor(1),
              barPercentage: 0.8,
              categoryPercentage: 0.85,
              barThickness: 100,
              borderRadius: { topLeft: 6, topRight: 6 },
              borderSkipped: false,
            },
          ],
        });
      } else {
        //for defect category chart
        const sortedEntries = Object.entries(this.dataset).sort(
          (a: any, b: any) => b[1] - a[1]
        );

        this.labels = sortedEntries.map(([key, _]) =>
          this.capitalizeFirstLetter(key)
        );

        this.values = Object.values(this.dataset);

        return (this.chartData = {
          labels: this.labels,
          datasets: [
            {
              data: this.values,
              backgroundColor: this.values.map((_, index) =>
                this.getDynamicColor(index)
              ),
              barPercentage: 0.8,
              categoryPercentage: 0.85,
              barThickness: 100,
              borderRadius: { topLeft: 6, topRight: 6 },
              borderSkipped: false,
            },
          ],
        });
      }
    }
  }

  /*
   *capitalize the first letter of each word, and if word has 2 chars, capitalize both
   */
  capitalizeFirstLetter(word: string): string {
    return word
      .split(/[\s_-]+/)
      .map((w) => {
        if (w.length === 2) {
          return w.toUpperCase();
        }
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(' ');
  }

  /**
   * Generates a dynamic color based on the index.
   * @param {number} index - The index for which to generate the color.
   * @returns {string} - The dynamic color.
   */
  getDynamicColor(index: number): string {
    const colors = [
      this.rootStyles.getPropertyValue("--royal-purple-20").trim()
    ];
    return colors[index % colors.length];
  }

  /**
   * Checks whether the provided object is empty.
   * @param {object} obj - The object to check.
   * @returns {boolean} - True if the object is empty, false otherwise.
   */
  isObjectEmpty(obj: any): boolean {
    return obj && Object.keys(obj).length === 0;
  }

  /**
   * Rounds a number up to the nearest multiple of a given factor.
   * @param {number} num - The number to round.
   * @param {number} factor - The factor to round to.
   * @returns {number} - The rounded number.
   */
  roundUpToNearest(num: number, factor: number) {
    return Math.ceil(num / factor) * factor;
  }

  /**
   * Processes and calculates ranges for data binning.
   * @param {object} dataset - The dataset to process.
   * @returns {object} - The calculated ranges with counts.
   */
  getRanges(dataset: any) {
    const counts = Object.values(dataset).map((item: any) => item.count);
    const maxCount = Math.max(...counts); // get maximum count in the dataset
    const maxRounded = this.roundUpToNearest(
      maxCount,
      10 ** Math.floor(Math.log10(maxCount))
    ); // Round the max count

    const step = maxRounded / 10; // get step size (maxRounded / 10)

    const result: Record<string, number> = {};
    const dynamicRanges: { start: number; end: number; label: string }[] = [];
    for (let i = 0; i <= maxRounded; i += step) {
      const start = i;
      const end = i + step - 1;
      const rangeLabel = end < maxRounded ? `${start}-${end}` : `${start}+`;
      result[rangeLabel] = 0;
      dynamicRanges.push({ start, end, label: rangeLabel });
    }

    Object.values(dataset).forEach((item: any) => {
      const count = item.count;

      // Find range
      const range = dynamicRanges.find(({ start, end }) =>
        end < maxRounded ? count >= start && count <= end : count >= start
      );

      if (range) {
        result[range.label]++;
      }
    });

    return result;
  }
}
